import { STORAGE_KEYS } from '../config/app.config';
import { PUBLIC_SITE_URL } from '../utils/meetingShareUrl';

const SITE_AUTH_SYNC_TTL_MS = 120_000;
let lastSyncSignature = '';
let lastSuccessfulSyncAt = 0;
let inFlightSync = null;

function readJson(key) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function isAllowedSiteMessage(event) {
  if (!event?.origin || !PUBLIC_SITE_URL) return false;
  try {
    return event.origin === new URL(PUBLIC_SITE_URL).origin;
  } catch {
    return event.origin === PUBLIC_SITE_URL;
  }
}

/**
 * همگام‌سازی سشن پنل با کوکی‌های سایت (safemeet.ir) برای SSO در صفحه join.
 * credentials: include اجباری است تا مرورگر Set-Cookie را ذخیره کند.
 */
export async function syncSiteAuthSession({ force = false } = {}) {
  if (typeof window === 'undefined') return false;

  const token = window.localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  if (!token) {
    await clearSiteAuthSession();
    return false;
  }

  const organizationId = window.localStorage.getItem(STORAGE_KEYS.CURRENT_ORG_ID);
  const adminToken = window.localStorage.getItem(STORAGE_KEYS.ADMIN_AUTH_TOKEN);
  const adminUser = readJson(STORAGE_KEYS.ADMIN_USER);
  const payload = {
    token,
    organizationId: organizationId || null,
    adminToken: adminToken || null,
    adminUser: adminUser || null,
  };
  const signature = JSON.stringify(payload);
  const recentlySynced =
    lastSyncSignature === signature && Date.now() - lastSuccessfulSyncAt < SITE_AUTH_SYNC_TTL_MS;

  if (!force && recentlySynced) return true;
  if (!force && inFlightSync) return inFlightSync;

  inFlightSync = (async () => {
    const res = await fetch(`${PUBLIC_SITE_URL}/api/auth/sync`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    const ok = res.ok && json.success === true;
    if (ok) {
      lastSyncSignature = signature;
      lastSuccessfulSyncAt = Date.now();
    }
    return ok;
  })();

  try {
    return await inFlightSync;
  } catch {
    return false;
  } finally {
    inFlightSync = null;
  }
}

/** باز کردن لینک join سایت پس از همگام‌سازی سشن پنل */
export async function openSiteJoinUrl(url) {
  if (!url || typeof window === 'undefined') return;
  await syncSiteAuthSession({ force: true });
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** پاک کردن کوکی‌های سایت هنگام خروج از پنل */
export async function clearSiteAuthSession() {
  if (typeof window === 'undefined') return;
  lastSyncSignature = '';
  lastSuccessfulSyncAt = 0;

  try {
    await fetch(`${PUBLIC_SITE_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    // silent
  }
}

/** پاسخ به درخواست sync از تب سایت (مثلاً صفحه join بازشده از پنل) */
export function installSiteSyncMessageListener() {
  if (typeof window === 'undefined') return () => {};

  const onMessage = async (event) => {
    if (!isAllowedSiteMessage(event)) return;
    if (event.data?.type !== 'safemeet:request-site-sync') return;
    if (!window.localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)) return;

    await syncSiteAuthSession();

    if (event.source && typeof event.source.postMessage === 'function') {
      event.source.postMessage({ type: 'safemeet:site-sync-done' }, event.origin);
    }
  };

  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}
